import { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import {
  addMessage,
  fetchConversations,
  fetchGroupMessages,
  setOnlineUsers,
  userOnline,
  userOffline,
  clearConversationUnread,
  upsertConversationPreview,
  reactivateGroupMembers,
  setConversationMembership,
} from "../store/slices/chatSlice";
import { addNotification } from "../store/slices/notificationSlice";
import rbacService from "../services/rbacService";
import { store } from "../store";
import { fetchMyPermissions } from "../store/slices/permissionsSlice";
import { queryClient } from "../lib/queryClient";
import { queryKeys } from "./queries/queryKeys";
import {
  getTokenSessionId,
  tokenIdentity,
  decideDeviceSessionsClearedLogout,
  recordLastLogoutCode,
} from "../utils/sessionRevocation";
import { installRoomReplay } from "../utils/socketRoomReplay";
import { decodeJwtPayload } from "../utils/jwtClock";
import { REFRESHABLE_AUTH_CODES } from "../services/authCodes";
import { refreshAccessTokenSingleFlight } from "../services/api";

/** Min interval between socket-triggered refreshes (design §4.7, FR-7.4). */
const SOCKET_REFRESH_THROTTLE_MS = 30_000;

const DEFAULT_REVOKED_COPY =
  "Your session ended because another device was approved. Please sign in again.";

/** Logout copy by revocation reason (design §3.2). Never throws. */
const logoutCopyForReason = (reason) => {
  switch (reason) {
    case "student_single_session":
      return "You signed in on another device. Please sign in again.";
    case "logout":
    case "revoked_by_user":
      return "You were signed out.";
    case "device_access_changed":
    default:
      return DEFAULT_REVOKED_COPY;
  }
};

/** Socket handshake auth payload (design §4.5 Web socket). */
const buildWebSocketAuth = (token) => ({
  token: token || "",
  platform: "web",
  ...(process.env.REACT_APP_BUILD_NUMBER ? { build: process.env.REACT_APP_BUILD_NUMBER } : {}),
});

let appSocketRef = null;
export const getAppSocket = () => appSocketRef;

const SOCKET_URL = (process.env.REACT_APP_API_URL || "http://localhost:4152")
  .replace(/\/api\/v1\/?$/, "")
  .replace(/\/+$/, "");

export const useSocket = (options = {}) => {
  const { onNewNotification, onNewMessage } = options;
  const dispatch = useDispatch();
  const authUser = useSelector((s) => s.auth?.user);
  const authLoading = useSelector((s) => s.auth?.loading);
  const onNewNotificationRef = useRef(onNewNotification);
  const onNewMessageRef = useRef(onNewMessage);
  useEffect(() => {
    onNewNotificationRef.current = onNewNotification;
    onNewMessageRef.current = onNewMessage;
  });

  const socketRef = useRef(null);
  const permissionsRefreshTimerRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});

  useEffect(() => {
    if (authLoading) return undefined;
    const token = localStorage.getItem("accessToken");
    if (!token || !authUser?.id) return undefined;

    // { userId, sessionId } of the token last presented in a handshake (design §4.7 Web).
    let handshakeIdentity = null;

    const socket = io(SOCKET_URL, {
      // Function form: every (re)connect attempt reads the CURRENT token, so a
      // refresh never requires recreating this instance (FR-7.1 / FR-7.2).
      auth: (cb) => {
        try {
          const t = localStorage.getItem("accessToken") || "";
          handshakeIdentity = tokenIdentity(t);
          cb(buildWebSocketAuth(t));
        } catch (e) {
          console.warn("[socket:auth] Error:", e?.message || e);
          cb({ token: "" });
        }
      },
      // Polling first to bypass Apache websocket restriction in production.
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });

    socketRef.current = socket;
    appSocketRef = socket;

    // Replay chat room joins on every reconnect (FR-7.3). Installed before the
    // first connect, so only genuine reconnects replay.
    const uninstallRoomReplay = installRoomReplay(socket, {
      join: ["join_conversation", "join_broadcast"],
      leaveMap: { leave_conversation: "join_conversation" },
    });

    // Same-user session change → re-handshake the SAME instance so the
    // server-side session_{sid} room follows the current token (FR-7.5).
    const onMaybeSessionChanged = () => {
      try {
        const cur = tokenIdentity(localStorage.getItem("accessToken"));
        if (!cur) return; // logged out: the logout path owns teardown
        if (
          handshakeIdentity?.userId != null &&
          String(cur.userId) !== String(handshakeIdentity.userId)
        ) {
          return; // user switch: the effect (deps on authUser.id) owns it
        }
        if (!socket.connected) {
          socket.connect(); // the auth fn picks up the current token
          return;
        }
        if (handshakeIdentity && cur.sessionId && cur.sessionId !== handshakeIdentity.sessionId) {
          socket.disconnect();
          socket.connect(); // same instance: listeners and 'connect' rejoins survive
        }
        // same sessionId (a refresh): no-op for a connected socket (FR-7.1)
      } catch (e) {
        console.warn("[socket:session-changed] Error:", e?.message || e);
      }
    };
    const onStorage = (e) => {
      try {
        if (e?.key === "accessToken") onMaybeSessionChanged();
      } catch (err) {
        console.warn("[socket:storage] Error:", err?.message || err);
      }
    };
    const onVisibilityChange = () => {
      try {
        if (
          document.visibilityState === "visible" &&
          !socket.connected &&
          localStorage.getItem("accessToken")
        ) {
          socket.connect(); // revives a socket whose reconnection attempts were exhausted
        }
      } catch (e) {
        console.warn("[socket:visibilitychange] Error:", e?.message || e);
      }
    };
    window.addEventListener("myams:access-token-refreshed", onMaybeSessionChanged);
    window.addEventListener("myams:session-changed", onMaybeSessionChanged);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibilityChange);

    // A refreshable handshake failure triggers one throttled single-flight
    // refresh. Socket errors never log out (FR-7.4).
    let lastSocketRefreshAt = 0;
    socket.on("connect_error", (err) => {
      try {
        const code = err?.data?.code;
        if (typeof code !== "string" || !REFRESHABLE_AUTH_CODES.includes(code)) return;
        const now = Date.now();
        if (now - lastSocketRefreshAt < SOCKET_REFRESH_THROTTLE_MS) return;
        lastSocketRefreshAt = now;
        Promise.resolve(refreshAccessTokenSingleFlight())
          .then((r) => {
            try {
              if (r?.status === "success" && !socket.connected) socket.connect();
            } catch (e) {
              console.warn("[socket:connect_error] reconnect failed:", e?.message || e);
            }
          })
          .catch((e) => {
            console.warn("[socket:connect_error] refresh failed:", e?.message || e);
          });
      } catch (e) {
        console.warn("[socket:connect_error] Error:", e?.message || e);
      }
    });

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("get_online_users");
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    socket.io.on("reconnect_attempt", () => {
      setConnected(false);
    });

    socket.io.on("reconnect", () => {
      setConnected(true);
      socket.emit("get_online_users");
    });

    socket.on("new_message", (message) => {
      const currentUserId = (() => {
        try {
          const token = localStorage.getItem("accessToken");
          if (!token) return null;
          return decodeJwtPayload(token)?.id ?? null;
        } catch (e) {
          console.warn("[socket:new_message] token decode failed:", e?.message || e);
          return null;
        }
      })();
      const isOwn =
        currentUserId != null && Number(message.senderId) === Number(currentUserId);
      const convId = message.conversationId;
      const hadConversation = (store.getState().chat.conversations || []).some(
        (c) => c?.id != null && String(c.id) === String(convId)
      );
      dispatch(addMessage({ ...message, isOwn }));
      if (!hadConversation) {
        dispatch(fetchConversations({ silent: true }));
      }

      if (!isOwn) {
        const activeConversation = store.getState().chat.activeConversation;
        if (activeConversation != null && String(activeConversation) === String(convId)) {
          return;
        }
        if (typeof onNewMessageRef.current === "function") onNewMessageRef.current(message);
      }
    });

    socket.on("support_conversation_created", (conversation) => {
      if (!conversation?.id) return;
      dispatch(upsertConversationPreview(conversation));
    });

    socket.on("new_notification", (notification) => {
      dispatch(addNotification(notification));
      const meta = notification?.metadata || notification?.meta || {};
      const requestType = meta.requestType || meta.entityType;
      const isProfileApproval =
        requestType === "teacher_profile_update" ||
        (meta.entityType === "profileUpdateRequest" && meta.status === "approved");
      if (isProfileApproval) {
        queryClient.invalidateQueries({ queryKey: ["teacherProfileDetails"] });
        queryClient.invalidateQueries({ queryKey: ["dashboardTeacherDetails"] });
      }
      if (typeof onNewNotificationRef.current === "function") onNewNotificationRef.current(notification);
    });

    socket.on("conversation_membership_changed", (payload) => {
      if (!payload?.conversationId) return;
      dispatch(
        setConversationMembership({
          conversationId: payload.conversationId,
          isActiveMember: payload.isActiveMember,
        })
      );
      if (payload.isActiveMember === false) {
        dispatch(fetchConversations({ silent: true }));
      }
    });

    socket.on("group_members_added", (payload) => {
      if (!payload?.conversationId && !payload?.groupId) return;
      const convId = payload.conversationId ?? payload.groupId;
      const authUser = store.getState().auth?.user;
      const memberIds = (payload.memberIds || []).map((id) => Number(id));
      if (memberIds.length) {
        dispatch(
          reactivateGroupMembers({
            conversationId: convId,
            memberIds,
            member:
              authUser?.id != null && memberIds.includes(Number(authUser.id))
                ? {
                    id: authUser.id,
                    firstName: authUser.firstName,
                    lastName: authUser.lastName,
                    fullName: authUser.fullName,
                    name:
                      authUser.fullName ||
                      `${authUser.firstName || ""} ${authUser.lastName || ""}`.trim() ||
                      "You",
                    profileImage: authUser.profileImage,
                    role: authUser.role,
                  }
                : null,
          })
        );
        if (authUser?.id != null && memberIds.includes(Number(authUser.id))) {
          dispatch(
            setConversationMembership({
              conversationId: convId,
              isActiveMember: true,
            })
          );
        }
      }
      dispatch(fetchConversations({ silent: true }));
      queryClient.invalidateQueries({ queryKey: [...queryKeys.chat.groups(), convId] });

      const currentUserId = authUser?.id;
      const activeConversation = store.getState().chat.activeConversation;
      if (
        currentUserId != null &&
        memberIds.includes(Number(currentUserId)) &&
        activeConversation != null &&
        String(activeConversation) === String(convId)
      ) {
        dispatch(fetchGroupMessages({ groupId: convId }));
      }
    });

    // Backend also emits chat_group_members_added for broadcast/community ChatGroups
    socket.on("chat_group_members_added", (payload) => {
      if (!payload?.conversationId && !payload?.groupId) return;
      dispatch(fetchConversations({ silent: true }));
      const authUser = store.getState().auth?.user;
      const memberIds = (payload.memberIds || []).map((id) => Number(id));
      if (authUser?.id != null && memberIds.includes(Number(authUser.id)) && payload.conversationId) {
        dispatch(
          setConversationMembership({
            conversationId: payload.conversationId,
            isActiveMember: true,
          })
        );
      }
    });

    socket.on("online_users", (users) => {
      dispatch(setOnlineUsers(users));
    });

    socket.on("user_status", ({ userId, status }) => {
      if (status === "online") {
        dispatch(userOnline(userId));
      } else {
        dispatch(userOffline(userId));
      }
    });

    socket.on("typing", ({ userId, conversationId }) => {
      setTypingUsers((prev) => ({ ...prev, [conversationId]: userId }));
    });

    socket.on("stop_typing", ({ conversationId }) => {
      setTypingUsers((prev) => {
        const next = { ...prev };
        delete next[conversationId];
        return next;
      });
    });

    socket.on("message_read", ({ userId, conversationId }) => {
      if (conversationId != null) {
        dispatch(clearConversationUnread(conversationId));
      }
    });

    const refreshPermissionsFromServer = () => {
      dispatch(fetchMyPermissions());
      queryClient.invalidateQueries({ queryKey: queryKeys.rbac.permissions() });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.rbac.permissions(), "matrix"] });
    };

    socket.on("permissions.updated", () => {
      if (permissionsRefreshTimerRef.current) {
        clearTimeout(permissionsRefreshTimerRef.current);
      }
      permissionsRefreshTimerRef.current = setTimeout(() => {
        refreshPermissionsFromServer();
      }, 400);
    });

    const forceLogoutFromRevokedSession = (sessionId, code, reason) => {
      try {
        const token2 = localStorage.getItem("accessToken");
        // Must stay first: the echo of a user-initiated logout (tokens already
        // cleared) is a no-op and can never write a reason (design §3.2).
        if (!token2) return;
        const cur = getTokenSessionId(token2);
        if (sessionId && cur && cur !== sessionId) return;
        recordLastLogoutCode(code || "SOCKET_SESSION_REVOKED");
        localStorage.setItem("logoutReason", logoutCopyForReason(reason));
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("selectedInstitute");
        localStorage.removeItem("isInInstituteContext");
        window.location.href = "/login";
      } catch (e) {
        console.error("[socket:forceLogoutFromRevokedSession] Error:", e?.message || e);
      }
    };

    socket.on("auth_session_revoked", (payload) => {
      try {
        forceLogoutFromRevokedSession(
          payload?.sessionId ?? null,
          payload?.code || "SESSION_REVOKED",
          payload?.reason
        );
      } catch (e) {
        console.error("[socket:auth_session_revoked] Error:", e?.message || e);
      }
    });

    socket.on("device_sessions_cleared", (payload) => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) return;
        const cur = getTokenSessionId(accessToken);
        if (
          decideDeviceSessionsClearedLogout({
            currentSessionId: cur,
            keepSessionId: payload?.keepSessionId,
            sessionIds: payload?.sessionIds,
          })
        ) {
          forceLogoutFromRevokedSession(null, "SOCKET_DEVICE_SESSIONS_CLEARED", payload?.reason);
        }
      } catch (e) {
        console.error("[socket:device_sessions_cleared] Error:", e?.message || e);
      }
    });

    return () => {
      if (permissionsRefreshTimerRef.current) {
        clearTimeout(permissionsRefreshTimerRef.current);
      }
      try {
        window.removeEventListener("myams:access-token-refreshed", onMaybeSessionChanged);
        window.removeEventListener("myams:session-changed", onMaybeSessionChanged);
        window.removeEventListener("storage", onStorage);
        document.removeEventListener("visibilitychange", onVisibilityChange);
        uninstallRoomReplay();
      } catch (e) {
        console.warn("[socket:cleanup] Error:", e?.message || e);
      }
      appSocketRef = null;
      socket.disconnect();
      socketRef.current = null;
    };
  }, [dispatch, authLoading, authUser?.id]);

  const joinConversation = useCallback((conversationId) => {
    socketRef.current?.emit("join_conversation", conversationId);
  }, []);

  const leaveConversation = useCallback((conversationId) => {
    socketRef.current?.emit("leave_conversation", conversationId);
  }, []);

  const joinBroadcast = useCallback((groupId) => {
    socketRef.current?.emit("join_broadcast", groupId);
  }, []);

  const emitTyping = useCallback((conversationId) => {
    socketRef.current?.emit("typing", { conversationId });
  }, []);

  const emitStopTyping = useCallback((conversationId) => {
    socketRef.current?.emit("stop_typing", { conversationId });
  }, []);

  const markRead = useCallback(
    (conversationId) => {
      if (conversationId == null) return;
      dispatch(clearConversationUnread(conversationId));
      socketRef.current?.emit("message_read", { conversationId });
    },
    [dispatch]
  );

  return {
    connected,
    typingUsers,
    joinConversation,
    leaveConversation,
    joinBroadcast,
    emitTyping,
    emitStopTyping,
    markRead,
  };
};
